import React, { ReactNode } from 'react'

import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Icon, { IconProps } from 'components_new/atoms/Icon'
import LinkWrapper from 'components_new/atoms/LinkWrapper'
import Text from 'components_new/atoms/Text'
import IconTip, { IconTipProps } from '../IconTip'

export interface SectionHeaderProps {
  actions?: ReactNode
  body?: ReactNode
  color?: 'disabled' | 'primary' | 'secondary'
  divider?: boolean
  gutterBottom?: boolean
  helperText?: ReactNode
  icon?: ReactNode | IconProps
  iconTip?: IconTipProps
  listItem?: boolean
  onClick?: () => void
  search?: string
  size?: 'xsmall' | 'small' | 'medium' | 'large'
  sx?: SxProps
  title: ReactNode
  titleAdornment?: ReactNode
  to?: string
}

const SectionHeader = React.forwardRef(
  (props: SectionHeaderProps, ref: any) => {
    return (
      <Box
        sx={{ ...(props.gutterBottom ? { mb: 1 } : null), ...props.sx }}
        ref={ref}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: props.actions ? 'space-between' : undefined
          }}
        >
          <Box sx={{ maxWidth: 'title' }}>
            <LinkWrapper href={props.to} search={props.search}>
              <Box
                onClick={props.onClick}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  cursor: props.to ? 'pointer' : undefined,
                  gap: props.size === 'large' ? 1 : 0.75,
                  '&:hover':
                    props.onClick || props.to
                      ? {
                          '& .SectionHeaderIcon': {
                            color: 'text.primary'
                          },
                          '& .SectionHeaderTitle': {
                            color: 'text.primary'
                          },
                          '& .SectionHeaderArrow': {
                            color: 'text.primary',
                            transform: 'translateX(0.5em)',
                            transition: 'all 0.2s ease'
                          }
                        }
                      : undefined
                }}
              >
                {props.icon &&
                typeof props.icon === 'object' &&
                'name' in props.icon ? (
                // eslint-disable-next-line max-len
                      <Icon
                        className="SectionHeaderIcon"
                        color={props.color}
                        fontSize={props.size === 'large' ? 'medium' : 'small'}
                        sx={{
                          ...(!props.icon.color
                            ? {
                                color: props.color
                                  ? `text.${props.color}`
                                  : 'text.primary'
                              }
                            : null)
                        }}
                        {...(props.icon as IconProps)}
                      />
                    ) : (
                      props.icon
                    )}
                <Box>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: props.size === 'large' ? 1 : 0.75
                    }}
                  >
                    <Text
                      className="SectionHeaderTitle"
                      color={
                        props.size === 'xsmall'
                          ? 'text.secondary'
                          : props.color
                            ? `text.${props.color}`
                            : 'text.primary'
                      }
                      sx={{
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical',
                        ...(props.size === 'xsmall' ? { mb: 0.5 } : undefined)
                      }}
                      variant={
                        props.size === 'xsmall'
                          ? 'caption'
                          : props.size === 'medium'
                            ? 'subtitle1'
                            : props.size === 'large'
                              ? 'h5'
                              : 'subtitle2'
                      }
                    >
                      {props.title}
                      {props.to || props.onClick ? (
                        <Icon
                          className="SectionHeaderArrow"
                          name="ArrowForward"
                          sx={{
                            color: 'text.disabled',
                            fontSize: '1em',
                            transform: 'translateX(0.25em)',
                            verticalAlign: '-0.15em'
                          }}
                        />
                      ) : null}
                    </Text>
                    {props.iconTip ? <IconTip {...props.iconTip} /> : null}
                    {props.titleAdornment}
                  </Box>
                  {props.body ? (
                    <Text
                      color="text.secondary"
                      component="div"
                      variant="body1"
                      sx={{ mt: '0.25em' }}
                    >
                      {props.body}
                    </Text>
                  ) : null}
                </Box>
              </Box>
            </LinkWrapper>
            {props.helperText ? (
              <Text
                color="text.secondary"
                component="div"
                variant="caption"
                sx={{ mt: '0.25em' }}
              >
                {props.helperText}
              </Text>
            ) : null}
          </Box>
          {props.actions ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {props.actions}
            </Box>
          ) : null}
        </Box>
        {props.divider ? <Divider sx={{ mt: 1 }} /> : null}
      </Box>
    )
  }
)

SectionHeader.displayName = 'SectionHeader'
export default SectionHeader
